"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isOidcFieldForwardedTo = exports.OIDC_CARRIED_FIELD_NOT_FORWARDED = exports.OIDC_DROPPED_FIELDS = exports.OIDC_BLOCKED_FIELDS = exports.OIDC_CARRIED_FIELDS = exports.OIDC_RAW_SIGNUP_FIELD_CLASSIFICATION = void 0;
// Identity function that keeps the literal classification of each entry (a plain
// Record<keyof RawSignupFields, OidcFieldClassification> annotation would widen it) while still
// requiring an entry for every key of RawSignupFields and rejecting keys it does not have (so a
// field removed from RawSignupFields cannot linger here).
const classify = (c) => c;
exports.OIDC_RAW_SIGNUP_FIELD_CLASSIFICATION = classify({
    // Identity comes from the IdP (server-verified), not from the form or URL.
    firstName: 'n/a-oidc',
    lastName: 'n/a-oidc',
    email: 'n/a-oidc',
    // company and role are collected on the OIDC details form after the redirect.
    company: 'n/a-oidc',
    role: 'n/a-oidc',
    // OIDC has dedicated required fields for these (bound into the signed identity token, see
    // REQUIRED_COMPLETE_FIELDS in oidcSignupController.ts); they come from form state, not the URL.
    edition: 'n/a-oidc',
    cloud: 'n/a-oidc',
    region: 'n/a-oidc',
    country: 'n/a-oidc',
    // Regular-form input the OIDC details form does not collect.
    phoneNumber: 'n/a-oidc',
    // Anti-abuse and timing inputs gathered by the regular form or computed server side.
    recaptchaToken: 'n/a-oidc',
    formId: 'n/a-oidc',
    formTwoCompletionTime: 'n/a-oidc',
    radarSession: 'n/a-oidc', // created on the callback page at submit, sent as its own field
    isApprovedProgramSignup: 'n/a-oidc', // server-derived
    isTrustedSignup: 'n/a-oidc', // server-derived
    // The server applies ?abexperiment= at page load (abExperimentContext.ts sets the override
    // cookie), which is what decides whether OIDC renders at all. The copy in the regular signup
    // request body is a legacy no-op override, so there is nothing for OIDC to carry.
    abexperiment: 'n/a-oidc',
    // Mechanical params mirrored from the regular flow (validated by validateMechanicalParams).
    template: 'carried',
    lab: 'carried',
    workspaces: 'carried',
    account: 'carried',
    token: 'carried',
    E6CCEE4CEB: 'carried',
    referrer: 'carried',
    owner: 'carried',
    sdm: 'carried',
    // Marketing attribution.
    utm_adgroup: 'carried',
    utm_source: 'carried',
    utm_medium: 'carried',
    utm_campaign: 'carried',
    utm_content: 'carried',
    utm_term: 'carried',
    utm_region: 'carried',
    gclid: 'carried',
    // Sales-assisted (SE/AE emails, se_email/se_ldap account params, Salesforce sales-assist fields).
    eng: 'blocked',
    // AWS Marketplace (awsType is set from the awsmp param; ampt is the Marketplace token).
    awsType: 'blocked',
    ampt: 'blocked',
    // Not a URL param: the server derives `developers` from the Developer signup flow type. Making
    // OIDC honour that flow type is a separate PR; until then it must not be sent in the complete
    // request.
    developers: 'blocked',
    // Marketplace listing: landing page, Salesforce data-exchange attribution, VA2 region rule.
    // Later work makes this carried: flip this entry (see the file doc for what else changes).
    listing: 'blocked',
    // Event signup link token (`t`, which the API copies to `link_token`).
    t: 'blocked',
    link_token: 'blocked',
    // CoCo subscription promo: program approval check and claim.
    program: 'blocked',
    // app.snowflake.com marketplace guest experience.
    originPage: 'blocked',
    // Known gaps (see the file doc). None of these is a URL param.
    // The regular flow sends window.location.href. The OIDC controller hardcodes '' (rawFields and
    // Salesforce). Probably an intentional omission, since the full URL can hold tokens (`t`,
    // `token`, `account`), but nobody has recorded the decision. Ticket/decision pending.
    signupUrl: 'dropped',
    // The regular flow reads the Mutiny experience count and experiences from the
    // x-mutiny-experience-* request headers (signupController.ts) and logs them. OIDC never reads
    // them. Ticket/decision pending.
    mutinyExperienceCount: 'dropped',
    mutinyExperiences: 'dropped',
    // Marketing consent. The regular flow collects it on the signup form and
    // SalesforceController maps it to Marketing_Opt_in__c, Was_Given_Choice_to_Opt_In__c and
    // HasOptedOutOfEmail. OIDC sends none of it, so every OIDC lead has those fields unset.
    // Carrying it over is awaiting marketing sign-off.
    optInEmailAgreement: 'dropped',
    optOutEmailAgreement: 'dropped',
    givenEmailOptInChoice: 'dropped',
});
const fieldsClassifiedAs = (classification) => Object.keys(exports.OIDC_RAW_SIGNUP_FIELD_CLASSIFICATION).filter(field => exports.OIDC_RAW_SIGNUP_FIELD_CLASSIFICATION[field] === classification);
exports.OIDC_CARRIED_FIELDS = fieldsClassifiedAs('carried');
exports.OIDC_BLOCKED_FIELDS = fieldsClassifiedAs('blocked');
exports.OIDC_DROPPED_FIELDS = fieldsClassifiedAs('dropped');
/**
 * Which server-side sinks receive a carried field once the complete request has been validated:
 *  - rawFields:   the RawSnowflakeAccountCreationParams built by buildCreateAccountRawFields and
 *                 passed to DeploymentService.createSnowflakeAccount.
 *  - salesforce:  the fields built in runAsyncPostCreation and passed to
 *                 SalesforceController.handleEntityUpsert.
 * A carried field is expected at every sink. Listing a sink here is an explicit decision that the
 * sink does not receive it (for Salesforce, that SalesforceController does not read the field).
 * The API contract test asserts both directions, so this cannot drift from the code either way.
 */
exports.OIDC_CARRIED_FIELD_NOT_FORWARDED = {
    template: ['salesforce'],
    workspaces: ['salesforce'],
    token: ['salesforce'],
    E6CCEE4CEB: ['salesforce'],
};
const isOidcFieldForwardedTo = (field, sink) => { var _a; return !((_a = exports.OIDC_CARRIED_FIELD_NOT_FORWARDED[field]) !== null && _a !== void 0 ? _a : []).includes(sink); };
exports.isOidcFieldForwardedTo = isOidcFieldForwardedTo;
//# sourceMappingURL=oidcCarriedFields.js.map